// src/components/Sidebar.jsx
import { useState } from 'react';

const SCORE_COLOR = (s) =>
  s >= 8 ? '#7FCC96' : s >= 6 ? '#E0B870' : '#E88080';

const SCORE_BG = (s) =>
  s >= 8 ? 'rgba(64,160,96,0.2)' : s >= 6 ? 'rgba(200,150,50,0.2)' : 'rgba(200,80,80,0.2)';

export default function Sidebar({ matters, activeMatterId, onSelectMatter, onHome, onNewMatter, onSignOut, onAccount, user }) {
  const [sort, setSort] = useState('date');

  const active   = matters.filter(m => m.status === 'active');
  const awaiting = matters.filter(m => m.status === 'pending' && !m.matter_s3_key);

  const sorted = (list) => [...list].sort((a, b) => {
    if (sort === 'name')  return a.matter_name.localeCompare(b.matter_name);
    if (sort === 'score') return (b.litfit_score || 0) - (a.litfit_score || 0);
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const NavItem = ({ matter }) => {
    const isActive = matter.matter_id === activeMatterId;
    const score = matter.litfit_score;
    return (
      <div
        onClick={() => onSelectMatter(matter.matter_id)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 20px', cursor: 'pointer',
          fontSize: 13.5, fontWeight: 400,
          color: isActive ? '#FFF' : 'rgba(255,255,255,0.42)',
          background: isActive ? 'rgba(200,168,75,0.1)' : 'transparent',
          borderLeft: isActive ? '2px solid #C8A84B' : '2px solid transparent',
          transition: 'all 0.15s', userSelect: 'none',
        }}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.color = 'rgba(255,255,255,0.78)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}}
        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.color = 'rgba(255,255,255,0.42)'; e.currentTarget.style.background = 'transparent'; }}}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.2"/>
        </svg>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {matter.matter_name}
        </span>
        {score != null ? (
          <span style={{
            marginLeft: 'auto', fontSize: 11.5, fontWeight: 500,
            padding: '2px 8px', borderRadius: 10, flexShrink: 0,
            background: SCORE_BG(score), color: SCORE_COLOR(score),
          }}>{score.toFixed(1)}</span>
        ) : matter.status === 'pending' ? (
          <span style={{
            marginLeft: 'auto', fontSize: 10.5, fontWeight: 500,
            padding: '2px 8px', borderRadius: 10, flexShrink: 0,
            background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.35)',
          }}>Pending</span>
        ) : null}
      </div>
    );
  };

  const SortBtn = ({ val, label }) => (
    <button
      onClick={() => setSort(val)}
      style={{
        background: sort === val ? 'rgba(200,168,75,0.08)' : 'transparent',
        border: sort === val ? '1px solid rgba(200,168,75,0.5)' : '1px solid rgba(255,255,255,0.1)',
        color: sort === val ? '#C8A84B' : 'rgba(255,255,255,0.3)',
        fontSize: 9.5, fontWeight: 500, fontFamily: "'DM Sans', sans-serif",
        padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
        letterSpacing: '0.04em', transition: 'all 0.15s',
      }}
    >{label}</button>
  );

  const FooterLink = ({ label, onClick, danger }) => (
    <div
      onClick={onClick}
      style={{
        fontSize: 11.5, color: danger ? '#E88080' : 'rgba(255,255,255,0.35)',
        cursor: 'pointer', padding: '4px 0',
        transition: 'color 0.15s', userSelect: 'none',
      }}
      onMouseEnter={e => e.currentTarget.style.color = danger ? '#FF9999' : 'rgba(255,255,255,0.7)'}
      onMouseLeave={e => e.currentTarget.style.color = danger ? '#E88080' : 'rgba(255,255,255,0.35)'}
    >{label}</div>
  );

  return (
    <div style={{
      width: 252, minWidth: 252, background: '#0D1B2A',
      height: '100vh', display: 'flex', flexDirection: 'column', overflowY: 'auto',
    }}>
      {/* Brand */}
      <div style={{ padding: '24px 20px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ fontSize: 11, fontWeight: 300, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Calderbank.ai
        </div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 500, color: '#C8A84B', marginTop: 3 }}>
          LitFit™
        </div>
      </div>

      {/* Nav */}
      <div style={{ padding: '10px 0', flex: 1, overflowY: 'auto' }}>
        {/* Home */}
        <div
          onClick={onHome}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 20px', cursor: 'pointer', fontSize: 13.5,
            color: !activeMatterId ? '#FFF' : 'rgba(255,255,255,0.42)',
            background: !activeMatterId ? 'rgba(200,168,75,0.1)' : 'transparent',
            borderLeft: !activeMatterId ? '2px solid #C8A84B' : '2px solid transparent',
            transition: 'all 0.15s',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M1 7L8 1l7 6v8h-4v-4H5v4H1V7z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
          </svg>
          Home
        </div>

        {/* New Matter */}
        <div style={{ padding: '8px 20px' }}>
          <button
            onClick={onNewMatter}
            style={{
              width: '100%', background: 'rgba(200,168,75,0.12)',
              border: '1px solid rgba(200,168,75,0.25)', borderRadius: 7,
              color: '#C8A84B', fontSize: 12.5, fontWeight: 400,
              fontFamily: "'DM Sans', sans-serif", padding: '7px 0',
              cursor: 'pointer', letterSpacing: '0.02em', transition: 'all 0.15s',
            }}
          >+ New matter</button>
        </div>

        {/* Awaiting */}
        {awaiting.length > 0 && (
          <>
            <div style={{ padding: '14px 20px 5px', fontSize: 10, fontWeight: 500, color: 'rgba(255,255,255,0.18)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              Awaiting
            </div>
            {awaiting.map(m => <NavItem key={m.matter_id} matter={m} />)}
          </>
        )}

        {/* Active matters */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 20px 5px' }}>
          <span style={{ fontSize: 10, fontWeight: 500, color: 'rgba(255,255,255,0.18)', letterSpacing: '0.14em', textTransform: 'uppercase', flex: 1 }}>
            Matters
          </span>
          <div style={{ display: 'flex', gap: 3 }}>
            <SortBtn val="date" label="Date" />
            <SortBtn val="name" label="Name" />
            <SortBtn val="score" label="Score" />
          </div>
        </div>
        {sorted(active).map(m => <NavItem key={m.matter_id} matter={m} />)}

            </div>

      {/* Footer — user info + account + sign out */}
      <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.5)', fontWeight: 400, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user?.name || user?.email || ''}
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <FooterLink label="Account" onClick={onAccount} />
          <FooterLink label="Sign out" onClick={onSignOut} danger />
        </div>
      </div>
    </div>
  );
}
